/**
 * Step 1 of the draft review flow (OR-6288 C3 W1/W2): the name/upload panel —
 * the create-modal's layout rendered IN the review view, reusing the modal's
 * ``ConceptsFileUpload`` (hover tint, file row below the dropzone, Change
 * file) plus a revert affordance.
 *
 * State (picked file + name) is CONTROLLED by the review parent so it survives
 * step flips and the parent can gate step 2 (a newly picked file — even one
 * with the same name — is indeterminate and forces re-upload; Revert restores
 * the current linked list and unblocks step 2 without uploading).
 *
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { ConceptsFileUpload } from '@verantos-dev/feature-concept-groups-list';
import {
  downloadDraftExportXlsx,
  downloadUploadSource,
  useUploadConceptGroupFileSync,
  useValidatePreviewName,
} from '@verantos-dev/feature-epi-chat';

import { projectPageKeys } from '../lib/api/queryKeys';

// OR-6288 finding: name-length cap surfaced by the Step 1 counter (parity with
// the legacy create modal).
const NAME_MAX_LENGTH = 100;

type NameCheckStatus = 'idle' | 'checking' | 'available' | 'taken';

export interface ConceptGroupDraftStepOneProps {
  previewId: string;
  projectId: string;
  /** Literal uploads re-upload their source list; agent drafts export one. */
  isUpload: boolean;
  draftName: string;
  /** Size of the draft's current list — the linked-content row fallback. */
  conceptCount: number | null;
  /** The uploaded file's original name (W3) — shown + downloadable when present. */
  sourceFilename: string | null;
  /** Keys the stored raw file in S3 (W6); previewId is the legacy fallback. */
  sourceUploadId: string | null;
  /**
   * Called with the NEW draft's identifiers after a successful upload (W6). The
   * parent navigates by ``conceptGroupId`` (canonical route) or ``previewId``
   * (legacy preview route) depending on where it was mounted (OR-6288 finding #8).
   */
  onUploaded: (created: { previewId: string; conceptGroupId: string | null }) => void;
  /** Controlled by the review parent (persists across step flips). */
  name: string;
  onNameChange: (name: string) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  /** Create entry (W5): no draft exists yet — the primary is Upload-only,
   *  disabled until a file is picked (never "Back to review"). */
  requireFile?: boolean;
  onReturnToReview: () => void;
}

export const ConceptGroupDraftStepOne = ({
  previewId,
  projectId,
  isUpload,
  draftName,
  conceptCount,
  sourceFilename,
  sourceUploadId,
  onUploaded,
  name,
  onNameChange,
  file,
  onFileChange,
  requireFile = false,
  onReturnToReview,
}: ConceptGroupDraftStepOneProps) => {
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  // OR-6288 W6: the upload completes synchronously; when a conversation is
  // attached (?chat=) the file chip + draft card mirror into it silently.
  const chatSessionId = searchParams.get('chat');

  const { mutate: uploadFile, isPending, isError } = useUploadConceptGroupFileSync();
  const queryClient = useQueryClient();
  // UC-4.2/4.3 (OR-6288 fast-follow): re-uploading over an EXISTING draft
  // (``previewId`` non-empty) discards it server-side, so the primary action
  // gates on an explicit confirmation; Cancel discards nothing. There is no
  // per-draft "reviewed mappings" signal on the preview, so replacing an
  // existing draft ALWAYS confirms (the safe fallback).
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);

  // OR-6288 finding: live duplicate-name validation on the field (parity with the
  // legacy create modal) — as the researcher types, the name is checked against the
  // org/project catalog after a short debounce; the spinner shows while checking and
  // a taken name is flagged before Review. mutateAsync is referentially stable, so
  // depending on it won't loop. Fails open on a check error (Approve re-validates).
  const { mutateAsync: validatePreviewName } = useValidatePreviewName();
  const [nameStatus, setNameStatus] = useState<NameCheckStatus>('idle');
  const trimmedName = name.trim();
  useEffect(() => {
    if (!trimmedName) {
      setNameStatus('idle');
      return;
    }
    setNameStatus('checking');
    const handle = setTimeout(() => {
      validatePreviewName({ conceptGroupName: trimmedName, projectId })
        .then((isAvailable) => setNameStatus(isAvailable ? 'available' : 'taken'))
        .catch(() => setNameStatus('idle'));
    }, 400);
    return () => clearTimeout(handle);
  }, [trimmedName, projectId, validatePreviewName]);
  // W4: the agent draft's generated workbook builds lazily server-side — the
  // file-row icon shows a processing spinner during the first download.
  const [exportBusy, setExportBusy] = useState(false);
  const exportName = `${draftName || 'concept-group-draft'}.xlsx`;
  const handleExportDownload = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    try {
      await downloadDraftExportXlsx(previewId, exportName);
    } finally {
      setExportBusy(false);
    }
  };

  const startUpload = () => {
    if (!file) return;
    const replacesPreviewId = previewId || undefined;
    uploadFile(
      {
        projectId,
        file,
        sessionId: chatSessionId,
        name: name.trim() || undefined,
        // UC-4.1: the new draft supersedes the one this panel is mounted on —
        // the backend discards it once the new draft is minted.
        replacesPreviewId,
      },
      {
        onSuccess: (created) => {
          if (replacesPreviewId) {
            // The superseded Draft row must vanish without a manual refresh.
            // A chat-attached upload also gets the RESOURCE_UPDATED push, but
            // with no conversation attached this refetch is the only signal.
            queryClient.invalidateQueries({
              queryKey: projectPageKeys.artifactsForProject(projectId),
            });
            queryClient.invalidateQueries({
              queryKey: projectPageKeys.activityForProject(projectId),
            });
          }
          onFileChange(null);
          onUploaded({ previewId: created.previewId, conceptGroupId: created.conceptGroupId });
        },
      }
    );
  };

  const handlePrimary = () => {
    if (!file) {
      if (requireFile) return;
      // Nothing changed — just go back to the review.
      onReturnToReview();
      return;
    }
    if (previewId) {
      // UC-4.2: replacing an existing draft discards its review work — confirm first.
      setConfirmReplaceOpen(true);
      return;
    }
    // Create entry (no draft to supersede) — upload silently, as today.
    startUpload();
  };

  return (
    <Stack sx={{ paddingX: 3, paddingY: 2, gap: 2.5, maxWidth: 860 }} data-testid="cg-step1-panel">
      <Typography variant="body2" color="text.secondary">
        {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.intro' })}
      </Typography>

      <TextField
        size="small"
        label={formatMessage({ id: 'epiChat.conceptGroupPreview.step1.nameLabel' })}
        placeholder={formatMessage({ id: 'epiChat.conceptGroupPreview.step1.namePlaceholder' })}
        value={name}
        onChange={(event) => onNameChange(event.target.value)}
        error={nameStatus === 'taken'}
        helperText={
          nameStatus === 'taken'
            ? formatMessage({ id: 'epiChat.conceptGroupPreview.step1.nameExists' })
            : formatMessage(
                { id: 'epiChat.conceptGroupPreview.step1.charCount' },
                { count: name.length, max: NAME_MAX_LENGTH }
              )
        }
        inputProps={{ 'data-testid': 'cg-step1-name-input', maxLength: NAME_MAX_LENGTH }}
        InputProps={{
          endAdornment:
            nameStatus === 'checking' ? (
              <InputAdornment position="end">
                <CircularProgress size={16} data-testid="cg-step1-name-checking" />
              </InputAdornment>
            ) : undefined,
        }}
      />

      <ConceptsFileUpload
        selectedFile={file}
        onFileSelect={onFileChange}
        isLoading={isPending}
        onClear={file ? () => onFileChange(null) : undefined}
        clearLabel={formatMessage({ id: 'epiChat.conceptGroupPreview.step1.revert' })}
        linkedFile={
          isUpload && sourceFilename
            ? {
                // The real uploaded file — bytes stream from S3 on click.
                name: sourceFilename,
                onClick: () => downloadUploadSource(sourceUploadId ?? previewId, sourceFilename),
              }
            : isUpload && conceptCount !== null
            ? {
                // Pre-W3 drafts have no stored filename — fall back to the count row.
                name: formatMessage(
                  { id: 'epiChat.conceptGroupPreview.step1.currentList' },
                  { count: conceptCount }
                ),
                onClick: handleExportDownload,
              }
            : {
                // Agent draft (W4): the generated two-sheet workbook, built
                // lazily on first download (spinner while the server builds).
                name: exportName,
                processing: exportBusy,
                onClick: handleExportDownload,
              }
        }
      />

      {!isUpload && (
        // Agent-built draft: the "make a file" bridge — export the current
        // concepts (the CSV shape the upload accepts), tweak, re-upload.
        // W4 upgrades this to the generated two-sheet XLSX with a stored pointer.
        <Alert severity="info" data-testid="cg-step1-agent-note">
          {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.agentDraftNote' })}{' '}
          <Link
            component="button"
            type="button"
            onClick={handleExportDownload}
            data-testid="cg-step1-download-current"
          >
            {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.downloadCurrent' })}
          </Link>
        </Alert>
      )}

      {isError && (
        <Alert severity="error">
          {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.uploadError' })}
        </Alert>
      )}

      <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
        <Button
          variant="text"
          onClick={onReturnToReview}
          sx={{ textTransform: 'none' }}
          data-testid="cg-step1-cancel"
        >
          {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.cancel' })}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          component={Link}
          href="/assets/concept-group-file-upload-template.xlsx"
          rel="noopener noreferrer"
          sx={{ textTransform: 'none' }}
        >
          {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.downloadTemplate' })}
        </Button>
        <Button
          variant="contained"
          onClick={handlePrimary}
          disabled={isPending || (file ? !name.trim() : requireFile)}
          startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
          data-testid="cg-step1-upload-button"
        >
          {formatMessage({
            id: isPending
              ? 'epiChat.conceptGroupPreview.step1.uploading'
              : file || requireFile
              ? 'epiChat.conceptGroupPreview.step1.upload'
              : 'epiChat.conceptGroupPreview.step1.backToReview',
          })}
        </Button>
      </Stack>

      {/* UC-4.2/4.3: the replace-confirmation gate. Confirm proceeds with the
          upload (the backend discards the superseded draft); Cancel closes and
          discards NOTHING — the current draft stays intact. */}
      <Dialog
        open={confirmReplaceOpen}
        onClose={() => setConfirmReplaceOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="cg-step1-replace-confirm-dialog"
      >
        <DialogTitle>
          {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.replaceConfirm.title' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.replaceConfirm.body' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmReplaceOpen(false)}
            sx={{ textTransform: 'none' }}
            data-testid="cg-step1-replace-cancel-button"
          >
            {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.replaceConfirm.cancel' })}
          </Button>
          <Button
            onClick={() => {
              setConfirmReplaceOpen(false);
              startUpload();
            }}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none' }}
            data-testid="cg-step1-replace-confirm-button"
          >
            {formatMessage({ id: 'epiChat.conceptGroupPreview.step1.replaceConfirm.confirm' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
